/**
 * The editor's save indicator (`061-editor.md` §8, "The save indicator").
 *
 * **Four states, and no others.** Note the offline copy: *"Saved locally"*, in amber —
 * deliberately reassuring, because it IS safe. The draft is in IndexedDB and the outbox
 * holds the write. Telling the user their work is in danger when it is not is its own
 * kind of bug.
 *
 * The global sync indicator in the top bar (`110-design-system.md` §9) reports the same
 * outbox; this one is the editor's local view of it, where the user is actually looking.
 */
import { Check, RefreshCw, TriangleAlert, CloudOff } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/lib/i18n';
import type { MessageKey } from '@/lib/i18n';

export type SaveStateKind = 'saved' | 'saving' | 'local' | 'conflict';

const COPY_KEY: Record<SaveStateKind, MessageKey> = {
  saved: 'editor.saveState.saved',
  saving: 'editor.saveState.saving',
  local: 'editor.saveState.local',
  conflict: 'editor.saveState.conflict',
};

export function SaveState({
  kind,
  onOpenConflict,
}: {
  kind: SaveStateKind;
  onOpenConflict: () => void;
}) {
  const { t } = useTranslation();

  const content = (
    <>
      {kind === 'saved' && <Check className="size-3.5" strokeWidth={2} />}
      {kind === 'saving' && <RefreshCw className="size-3.5 animate-spin" strokeWidth={2} />}
      {kind === 'local' && <CloudOff className="size-3.5" strokeWidth={2} />}
      {kind === 'conflict' && <TriangleAlert className="size-3.5" strokeWidth={2} />}
      <span>{t(COPY_KEY[kind])}</span>
    </>
  );

  // Every state has a distinct icon AND distinct text. Colour is a reinforcement, never
  // the signal — the indicator is legible in greyscale and in a screenshot (110 §1.3).
  const className = cn(
    'flex items-center gap-1 text-xs',
    kind === 'local' && 'text-[var(--warning)]',
    kind === 'conflict' && 'text-[var(--destructive)]',
  );

  if (kind === 'conflict') {
    return (
      <button
        type="button"
        data-testid="editor-save-state"
        onClick={onOpenConflict}
        className={cn(className, 'underline underline-offset-2')}
      >
        {content}
      </button>
    );
  }

  return (
    <span data-testid="editor-save-state" className={className}>
      {content}
    </span>
  );
}
